import React from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";

import { TopBarSelectors } from "src/redux/TopBar/selectors";
import { ROLE, VIEWS } from "src/redux/TopBar/constants";

import styles from "./styles.module.scss";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";

const ViewSwitch = () => {
    const role = useSelector(TopBarSelectors.Role);
    const userRole = useSelector(AuthSelectors.UserRole);
    const {
        "enable-recall-trade": enabledRecallTrade,
        "enable-post-trade": enabledPostTrade,
        "limited-ui": limitedUi,
    } = useSelector(SettingsSelectors.FeatureFlags);
    const usesClientPool = useSelector(AuthSelectors.UsesClientPool);

    // Limited UI relabels per requirements §4:
    //   - Lender: "Inventory" -> "Outbox"
    //   - Both:   "Negotiations" -> "Message"
    // The borrower's own tab is "Inbox" whatever the flag says, so it carries an
    // explicit label rather than deriving one from the view name.
    //   - Confirmations: no change
    // The label defaults below derive from view[0].toUpperCase()+view.slice(1)
    // ("negotiations" -> "Negotiations", "inventory" -> "Inventory"). We
    // override only when limitedUi is true. Default false: every tab keeps
    // its current label.
    const inventoryLabel =
        limitedUi && role === ROLE.LENDER ? "Outbox" : undefined;
    const transactionsLabel = limitedUi ? "Message" : undefined;

    // Admin portal roles only see their specific view
    const allTabs = userRole === "supervisor"
        ? [{ view: VIEWS.DASHBOARD, visible: true, label: "Statistics" }]
        : userRole === "operator"
        ? [
              { view: VIEWS.CPTIES, visible: true, label: "Counterparties" },
              { view: VIEWS.USERS, visible: true, label: "Users" },
              { view: VIEWS.FEATURE_FLAGS, visible: true, label: "Feature Flags" },
              { view: VIEWS.PLATFORM, visible: true, label: "Platform" },
              { view: VIEWS.SECURITY, visible: true, label: "Security" },
              { view: VIEWS.AUDIT, visible: true, label: "Audit" },
              // Grafana is served by the admin ingress on this same host, behind
              // the same operator session, so it is a link out rather than a
              // route: the SPA has nothing of its own to render for it. The path
              // is relative, so it follows whichever admin host is serving the
              // portal without the environment having to be named.
              { href: "/grafana/", visible: true, label: "Grafana" },
          ]
        : [
              { view: VIEWS.TRANSACTIONS, visible: true, label: transactionsLabel },
              {
                  view: VIEWS.CLIENT_POOL,
                  visible: role === ROLE.LENDER && usesClientPool,
              },
              { view: VIEWS.INVENTORY, visible: role === ROLE.LENDER, label: inventoryLabel },
              { view: VIEWS.AVAILABILITY, visible: role === ROLE.BORROWER, label: "Inbox" },
              { view: VIEWS.CONFIRMATIONS, visible: true, label: "Records" },
              { view: VIEWS.POST_TRADE, visible: enabledPostTrade },
              { view: VIEWS.RECALLS, visible: enabledRecallTrade },
              { view: VIEWS.DASHBOARD, visible: true, label: "Statistics" },
          ];

    return (
        <div className={styles.switchView}>
            {allTabs.map(({ view, visible, label, href }) => {
                if (!visible) return null;

                const btnName = label || view[0].toUpperCase() + view.slice(1);
                // Opened in its own tab: Grafana is a whole application, and
                // replacing the portal with it would strand the operator with
                // only the back button to return.
                if (href) {
                    return (
                        <a
                            href={href}
                            key={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="grafana-tab"
                        >
                            <span>{btnName}</span>
                        </a>
                    );
                }
                return (
                    <NavLink
                        to={`/${view}`}
                        key={view}
                        data-testid={`${view}-tab`}
                        activeClassName={styles.active}
                    >
                        <span>{btnName}</span>
                    </NavLink>
                );
            })}
        </div>
    );
};

export default React.memo(ViewSwitch);
